import { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, getRedirectResult } from 'firebase/auth';
import { auth } from '../firebase';

const AuthContext = createContext(null);

// Mirrors the isAdmin() check in firestore.rules
const ADMIN_DOMAINS = ['digimill.ie', 'millermail.ie', 'digimill.net'];

/**
 * Extract portal role info from Firebase ID token claims + email domain.
 * Returns { role, clubId, source } or null if no portal role.
 *
 * `source` records HOW the role was established, and it matters: firestore.rules
 * establishes portal roles ONLY from the `portalRole` custom claim
 * (`isGlobalAdmin()` / `isClubAdmin()`), so a role inferred here from an admin
 * email domain is real for routing but carries NO Firestore authority. A screen
 * that offers an action the rules will reject needs to tell the two apart —
 * see `isClaimGlobalAdmin` below.
 */
export function extractPortalRole(idTokenResult, email) {
  const claims = idTokenResult?.claims || {};
  const role = claims.portalRole || null;
  const clubId = claims.portalClubId || null;
  if (role) return { role, clubId, source: 'claim' };

  // Global admin: check email domain (mirrors Firestore isAdmin() rule).
  // NB this mirrors isAdmin(), NOT isGlobalAdmin() — the two are different
  // rules, and collections gated on the latter reject this user.
  const domain = email?.split('@')[1];
  if (domain && ADMIN_DOMAINS.includes(domain)) {
    return { role: 'GlobalAdmin', clubId: null, source: 'domain' };
  }

  return null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading
  const [portalRole, setPortalRole] = useState(null);

  useEffect(() => {
    // Handle redirect result from signInWithRedirect
    getRedirectResult(auth).catch((err) => {
      console.error('[AuthContext] getRedirectResult error:', err.code, err.message);
    });

    return onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const idTokenResult = await firebaseUser.getIdTokenResult();
          setPortalRole(extractPortalRole(idTokenResult, firebaseUser.email));
          setUser(firebaseUser);
        } catch (err) {
          console.error('[AuthContext] getIdTokenResult failed:', err);
          setUser(null);
          setPortalRole(null);
        }
      } else {
        setUser(null);
        setPortalRole(null);
      }
    });
  }, []);

  /** Force-refresh the ID token to pick up newly-set claims. */
  const refreshClaims = async () => {
    if (!auth.currentUser) return;
    const idTokenResult = await auth.currentUser.getIdTokenResult(true);
    setPortalRole(extractPortalRole(idTokenResult, auth.currentUser.email));
  };

  const value = {
    user,
    portalRole,
    loading: user === undefined,
    isGlobalAdmin: portalRole?.role === 'GlobalAdmin',
    // Global admin whose role came from the `portalRole` CLAIM — the only kind
    // firestore.rules' isGlobalAdmin() recognises. Use this, not isGlobalAdmin,
    // to decide whether to offer an action that a claim-gated rule must permit;
    // a domain-derived admin would be shown the control and then refused.
    isClaimGlobalAdmin: portalRole?.role === 'GlobalAdmin' && portalRole?.source === 'claim',
    isClubAdmin: portalRole?.role === 'ClubAdmin',
    isRaceOfficer: portalRole?.role === 'RaceOfficer',
    refreshClaims,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
